/**
 * App-level error boundary (U5). Catches render-time errors anywhere below it and
 * shows a recoverable fallback instead of a blank screen.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Title3, Body1, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: '440px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
  detail: { color: tokens.colorNeutralForeground3, wordBreak: 'break-word' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, justifyContent: 'center' },
});

function Fallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const styles = useStyles();
  return (
    <main className={styles.root} role="alert">
      <section className={styles.card}>
        <Title3>Something went wrong</Title3>
        <Body1>The interface hit an unexpected error. You can try again or reload the page.</Body1>
        <Body1 className={styles.detail}>{error.message}</Body1>
        <div className={styles.actions}>
          <Button appearance="primary" onClick={onReset}>
            Try again
          </Button>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </section>
    </main>
  );
}

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for diagnostics; a real deployment would forward this to telemetry.
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) return <Fallback error={this.state.error} onReset={this.reset} />;
    return this.props.children;
  }
}

export default ErrorBoundary;
